import {
	createContext,
	FC,
	MutableRefObject,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { FullPageError } from 'src/components/full-page-error/FullPageError';
import { Logger } from 'src/services/Logger';

import { isLoggedIn, isUserAuthProviderSaml } from '../../services/AuthenticationService';
import { getUserTenant } from '../../services/TenantsService';
import { getUserMetadata } from '../../services/UserService';
import { getRolesList } from '../../services/UsersService';
import { ITenant, ProductType, TenantProduct } from '../../types/Tenant';
import { IUser, RoleTypeItem } from '../../types/User';

import { runWithLoadingLock } from './UserTenantProvider.helpers';
import { USER_TENANT_PROVIDER_LABELS } from './UserTenantProvider.labels';

interface IUsersRolesListDictionary {
	[key: string]: string;
}

interface ILoadWithErrorStateParams<T> {
	shouldLoad: boolean;
	lockRef: MutableRefObject<boolean>;
	load: () => Promise<T>;
	errorMessage: string;
	onLoaded: (loadedValue: T) => void;
}

interface IUserTenant {
	user: IUser | null;
	tenant: ITenant | null;
	/**
	 * `true` once the user is loaded and the roles request has settled, including
	 * an empty list. Consumers that make authorisation decisions during render —
	 * most notably `withLimitedAccess` — must wait for this before trusting
	 * `hasAdminPermissions()`.
	 */
	isReady: boolean;
	hasAdminPermissions: () => boolean;
	updateUserTenant: (arg1?: boolean) => Promise<void>;
	usersRolesList: RoleTypeItem[];
	setUsersRolesList: (usersRolesList: RoleTypeItem[]) => void;
	getFullRoleItem: (role: string) => RoleTypeItem;
	usersRolesListDictionary: IUsersRolesListDictionary;
	getProductIdByType: (type: ProductType) => string;
}

const UserTenantContext = createContext<IUserTenant | null>(null);
const MISSING_ROLE_NAME_PREFIX = 'Unknown role';

export const UserTenantProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<IUser | null>(null);
	const [tenant, setTenant] = useState<ITenant | null>(null);
	const [usersRolesList, setUsersRolesList] = useState<RoleTypeItem[]>([]);
	const [hasLoadingError, setHasLoadingError] = useState(false);
	const [hasLoadedRoles, setHasLoadedRoles] = useState(false);

	const isUserLoading = useRef<boolean>(false);
	const isTenantLoading = useRef<boolean>(false);
	const isRolesLoading = useRef<boolean>(false);

	const getProductIdByType = useCallback(
		(productType: ProductType): string => {
			const product = tenant?.products?.find(
				(product: TenantProduct) => product.type === productType
			);
			return product?.id || '';
		},
		[tenant]
	);

	const getFullRoleItem = useCallback(
		(role: string): RoleTypeItem => {
			const roleItem = usersRolesList.find((item) => item.type === role);
			if (roleItem) {
				return roleItem;
			}

			Logger.warn('Role type not found in roles list', { role });
			return { id: role, name: `${MISSING_ROLE_NAME_PREFIX}: ${role}`, type: role };
		},
		[usersRolesList]
	);

	const loadWithErrorState = useCallback(
		async <T,>({
			shouldLoad,
			lockRef,
			load,
			errorMessage,
			onLoaded
		}: ILoadWithErrorStateParams<T>): Promise<void> => {
			if (!shouldLoad) {
				return;
			}

			try {
				const loadedValue = await runWithLoadingLock({
					lockRef,
					load,
					errorMessage
				});

				if (loadedValue) {
					onLoaded(loadedValue);
				}
			} catch (error) {
				setHasLoadingError(true);
				throw error;
			}
		},
		[]
	);

	const updateUserTenant = useCallback(
		async (force = false): Promise<void> => {
			if (!isLoggedIn()) {
				return;
			}

			setHasLoadingError(false);
			await loadWithErrorState({
				shouldLoad: !user || force,
				lockRef: isUserLoading,
				load: getUserMetadata,
				errorMessage: 'Failed to load user metadata',
				onLoaded: (currentUser) => {
					const isSSO = isUserAuthProviderSaml();
					setUser({ ...currentUser, isSSO });
				}
			});
			await loadWithErrorState({
				shouldLoad: !tenant || force,
				lockRef: isTenantLoading,
				load: getUserTenant,
				errorMessage: 'Failed to load user tenant',
				onLoaded: setTenant
			});
			await loadWithErrorState({
				// Length is the wrong signal: an empty list is a finished load, and a
				// second run can observe that empty list while the first still holds
				// the roles lock. Gate on the settled flag instead.
				shouldLoad: !hasLoadedRoles || force,
				lockRef: isRolesLoading,
				load: async () => {
					const roles = await getRolesList();
					return roles ?? [];
				},
				errorMessage: 'Failed to load user roles list',
				onLoaded: (roles) => {
					setUsersRolesList(roles);
					setHasLoadedRoles(true);
				}
			});
		},
		[loadWithErrorState, user, tenant, hasLoadedRoles]
	);

	const onLoad = useCallback(() => updateUserTenant(), [updateUserTenant]);

	// Derived synchronously from `usersRolesList` so it lands on the same render
	// as the list. A `useEffect`-driven dictionary would leave one tick where
	// `isReady` is already true and `hasAdminPermissions()` still sees an empty map.
	const usersRolesListDictionary = useMemo<IUsersRolesListDictionary>(() => {
		const dictionary: IUsersRolesListDictionary = {};
		usersRolesList.forEach((role) => {
			dictionary[role.type] = role.type;
		});
		return dictionary;
	}, [usersRolesList]);

	const hasAdminPermissions = useCallback(
		(): boolean => !!user && user.role?.type === usersRolesListDictionary?.TENANT_ADMIN,
		[user, usersRolesListDictionary]
	);

	useEffect(() => {
		onLoad().catch(() => {
			// The provider already logged and exposed the error state.
		});
	}, [onLoad]);

	// `runWithLoadingLock` returns immediately when another run holds the roles
	// lock, so that run's completion must not flip readiness. The flag is set
	// only by the run that received the response. An empty list still counts,
	// so `hasAdminPermissions()` can return false and the route can redirect.
	const isReady = Boolean(user) && hasLoadedRoles;

	const value = useMemo(() => {
		return {
			user,
			tenant,
			isReady,
			hasAdminPermissions,
			updateUserTenant,
			usersRolesList,
			setUsersRolesList,
			getFullRoleItem,
			usersRolesListDictionary,
			getProductIdByType
		};
	}, [
		user,
		tenant,
		isReady,
		hasAdminPermissions,
		updateUserTenant,
		usersRolesList,
		getFullRoleItem,
		usersRolesListDictionary,
		getProductIdByType
	]);
	if (hasLoadingError) {
		return <FullPageError title={USER_TENANT_PROVIDER_LABELS.loadingErrorTitle} />;
	}

	return <UserTenantContext.Provider value={value}>{children}</UserTenantContext.Provider>;
};

export const useUserTenant = (): IUserTenant => {
	const context = useContext(UserTenantContext);

	if (!context) {
		throw new Error('useUserTenant must be used within UserTenantProvider');
	}

	return context;
};
